<template>
  <main-section
    :isEditMode="isEditMode"
    :isDiff="service.isDiff"
    :isOutdated="isOutdated"
    :item="service"
    :itemType="'MAIL_SERVICE'"
    :typename="'service'"
    :onUpdate="updateService"
    :onCreate="createService"
    :onDelete="deleteService"
    @change-chosen="onChangeChosen"
    @revert="onRevert"
  >
    <fieldset slot="fieldset" :disabled="isOutdated || service.isDiff">
      <div class="two fields">
        <div
          class="field required"
          :class="{ error: $v.service.name.$error }"
          v-diff-field="serviceDiff.name"
        >
          <label>{{ $t('SERVICE_NAME') }}</label>
          <input
            type="text"
            name="serviceName"
            :placeholder="$t('SERVICE_NAME_PLACEHOLDER')"
            v-model="service.name"
            @click="$v.service.name.$touch()"
            :disabled="isOutdated || service.isDiff"
          />
        </div>
        <div
          class="field required"
          :class="{ error: $v.service.key.$error }"
          v-diff-field="serviceDiff.key"
        >
          <label>{{ $t('SERVICE_KEY') }}</label>
          <input
            type="text"
            name="serviceKey"
            :placeholder="$t('SERVICE_KEY_PLACEHOLDER')"
            v-model="service.key"
            @click="$v.service.key.$touch()"
            :disabled="isOutdated || service.isDiff"
          />
        </div>
      </div>

      <div class="inline fields">
        <!-- MAIL SERVICE TYPE -->
        <label>{{ $tc('MAIL_SERVICE_TYPE', 1) }}</label>
        <div
          class="field"
          v-for="(typeName, capitalized) in mailServiceTypes"
          :key="typeName"
          v-diff-field.disable="serviceDiff.serviceConfiguration.mailServiceType"
        >
          <div class="ui radio checkbox">
            <input
              type="radio"
              name="mailServiceType"
              :id="'mailServiceType' + typeName"
              :value="typeName"
              v-model="service.serviceConfiguration.mailServiceType"
            />
            <label :for="'mailServiceType' + typeName">
              {{ $tc('MAIL_SERVICE_TYPE_' + capitalized, 1) }}
            </label>
          </div>
        </div>
      </div>

      <template v-if="isImapMode">
        <h4 class="ui horizontal left aligned divider header">{{ $t('IMAP') }}</h4>
        <div class="ui basic segment">
          <div class="two fields">
            <div
              class="twelve wide field required"
              :class="{ error: $v.service.serviceConfiguration.imapHost.$error }"
              v-diff-field="serviceDiff.serviceConfiguration.imapHost"
            >
              <label>{{ $t('IMAP_HOST') }}</label>
              <input
                type="text"
                name="imapServer"
                :placeholder="$t('IMAP_HOST_PLACEHOLDER')"
                v-model="service.serviceConfiguration.imapHost"
                @click="$v.service.serviceConfiguration.imapHost.$touch()"
                :disabled="isOutdated || service.isDiff"
              />
            </div>
            <div
              class="four wide field required"
              :class="{ error: $v.service.serviceConfiguration.imapPort.$error }"
              v-diff-field="serviceDiff.serviceConfiguration.imapPort"
            >
              <label>{{ $t('IMAP_PORT') }}</label>
              <input
                type="number"
                name="imapPort"
                :placeholder="$t('IMAP_PORT_PLACEHOLDER')"
                v-model.number="service.serviceConfiguration.imapPort"
                @click="$v.service.serviceConfiguration.imapPort.$touch()"
                :disabled="isOutdated || service.isDiff"
              />
            </div>
          </div>
          <div class="two fields">
            <div
              class="field required"
              :class="{ error: $v.service.serviceConfiguration.imapUsername.$error }"
              v-diff-field="serviceDiff.serviceConfiguration.imapUsermame"
            >
              <label>{{ $t('IMAP_USERNAME') }}</label>
              <input
                type="text"
                name="imapUsername"
                :placeholder="$t('IMAP_USERNAME_PLACEHOLDER')"
                v-model="service.serviceConfiguration.imapUsername"
                @click="$v.service.serviceConfiguration.imapUsername.$touch()"
                :disabled="isOutdated || service.isDiff"
              />
            </div>
            <div class="field" v-diff-field="serviceDiff.serviceConfiguration.imapPassword">
              <label>{{ $t('IMAP_PASSWORD') }}</label>
              <password-input
                :placeholder="$t('IMAP_PASSWORD_PLACEHOLDER')"
                v-model="service.serviceConfiguration.imapPassword"
                :disabled="isOutdated || service.isDiff"
              />
            </div>
          </div>
        </div>

        <h4 class="ui horizontal left aligned divider header">{{ $t('SMTP') }}</h4>
        <div class="ui basic segment">
          <div class="two fields">
            <div
              class="twelve wide field required"
              :class="{ error: $v.service.serviceConfiguration.smtpHost.$error }"
              v-diff-field="serviceDiff.serviceConfiguration.smtpHost"
            >
              <label>{{ $t('SMTP_HOST') }}</label>
              <input
                type="text"
                name="smtpHost"
                :placeholder="$t('SMTP_HOST')"
                v-model="service.serviceConfiguration.smtpHost"
                @click="$v.service.serviceConfiguration.smtpHost.$touch()"
                :disabled="isOutdated || service.isDiff"
              />
            </div>
            <div
              class="four wide field required"
              :class="{ error: $v.service.serviceConfiguration.smtpPort.$error }"
              v-diff-field="serviceDiff.serviceConfiguration.smtpPort"
            >
              <label>{{ $t('SMTP_PORT') }}</label>
              <input
                type="number"
                name="smtpPort"
                :placeholder="$t('SMTP_PORT_PLACEHOLDER')"
                v-model.number="service.serviceConfiguration.smtpPort"
                @click="$v.service.serviceConfiguration.smtpPort.$touch()"
                :disabled="isOutdated || service.isDiff"
              />
            </div>
          </div>
          <div class="two fields">
            <div class="field" v-diff-field="serviceDiff.serviceConfiguration.smtpUsername">
              <label>{{ $t('SMTP_USERNAME') }}</label>
              <input
                type="text"
                name="smtpUsername"
                :placeholder="$t('SMTP_USERNAME_PLACEHOLDER')"
                v-model="service.serviceConfiguration.smtpUsername"
                :disabled="isOutdated || service.isDiff"
              />
            </div>
            <div class="field" v-diff-field="serviceDiff.smtpPassword">
              <label>{{ $t('SMTP_PASSWORD') }}</label>
              <password-input
                :placeholder="$t('SMTP_PASSWORD_PLACEHOLDER')"
                v-model="service.serviceConfiguration.smtpPassword"
                :disabled="isOutdated || service.isDiff"
              />
            </div>
          </div>
        </div>
      </template>

      <template v-if="isExchangeMode">
        <h4 class="ui horizontal left aligned divider header">
          {{ $t('MAIL_SERVICE_TYPE_EXCHANGE') }}
        </h4>
        <div class="ui basic segment">
          <div
            class="sixteen wide field"
            v-diff-field="serviceDiff.serviceConfiguration.exchangeServiceUrl"
          >
            <label>{{ $t('EXCHANGE_SERVICE_URL') }}</label>
            <input
              type="text"
              name="exchangeServiceUrl"
              :placeholder="$t('EXCHANGE_SERVICE_URL_PLACEHOLDER')"
              v-model="service.serviceConfiguration.exchangeServiceUrl"
              :disabled="isOutdated || service.isDiff"
            />
          </div>
          <div class="two fields">
            <div
              class="field required"
              :class="{ error: $v.service.serviceConfiguration.exchangeUser.$error }"
              v-diff-field="serviceDiff.serviceConfiguration.exchangeUser"
            >
              <label>{{ $t('EXCHANGE_USERNAME') }}</label>
              <input
                type="text"
                name="exchangeUser"
                :placeholder="$t('EXCHANGE_USERNAME_PLACEHOLDER')"
                v-model="service.serviceConfiguration.exchangeUser"
                @click="$v.service.serviceConfiguration.exchangeUser.$touch()"
                :disabled="isOutdated || service.isDiff"
              />
            </div>
            <div class="field" v-diff-field="serviceDiff.serviceConfiguration.exchangePass">
              <label>{{ $t('EXCHANGE_PASSWORD') }}</label>
              <password-input
                :placeholder="$t('EXCHANGE_PASSWORD_PLACEHOLDER')"
                v-model="service.serviceConfiguration.exchangePass"
                :disabled="isOutdated || service.isDiff"
              />
            </div>
          </div>

          <div class="field" v-diff-field="serviceDiff.serviceConfiguration.exchangeUseLegacyMode">
            <div class="ui checkbox">
              <input
                type="checkbox"
                id="exchangeUseLegacyMode"
                v-model="service.serviceConfiguration.exchangeUseLegacyMode"
                value="CLICK"
                :disabled="isOutdated || service.isDiff"
              />
              <label for="exchangeUseLegacyMode">
                {{ $t('EXCHANGE_USE_LEGACY_MODE') }}
              </label>
            </div>
          </div>
        </div>
      </template>

      <!-- Used in ... -->
      <template v-if="mode === 'edit'">
        <h4 class="ui horizontal left aligned divider header">
          {{ $t('USED_IN', { thing: $tc('FLOW', (usedIn && usedIn.length) || 0) }) }}
        </h4>
        <div class="ui basic segment">
          <div class="field" v-if="usedIn && usedIn.length > 0">
            <div class="ui link list" id="usedIn">
              <router-link
                class="item"
                :to="{ name: u.routeName, params: u.routeParams }"
                :key="u.id"
                v-for="u in usedIn"
              >
                <span>
                  <i class="share icon" v-bind:class="{ hidden: u.as === 'trigger' }" />
                  <span class="content">{{ u.name }}</span>
                </span>
              </router-link>
            </div>
          </div>
          <div v-else>
            <span class="text bold">{{ $t('SERVICE_NOT_USED') }}</span>
          </div>
        </div>
      </template>
    </fieldset>
  </main-section>
</template>

<style lang="scss" scoped>
@charset "utf-8";
@import '../../scss/variables';
</style>

<script>
// @flow
import Vue from 'vue'
import _ from 'lodash'
import { Service, Flow, Toast, Typenames, ErrorCodes } from 'services'
import { required, numeric, helpers, maxValue, and, or } from 'vuelidate/lib/validators'
import { MainSection, PasswordInput } from 'ui'
import { map } from 'rxjs/operators'
import { ActionTypes } from 'store'

const MailServiceType = {
  IMAPSMTP: 'imapsmtp',
  EXCHANGE: 'exchange'
}

const MailServiceTypeLabel = {
  imapsmtp: 'IMAP',
  exchange: 'Exchange'
}

const blankServiceConfiguration = {
  imapHost: '',
  imapPort: 0,
  imapUsername: '',
  imapPassword: '',
  smtpHost: '',
  smtpPort: 0,
  smtpUsername: '',
  smtpPassword: '',
  exchangeUser: '',
  exchangePass: '',
  exchangeServiceUrl: '',
  exchangeUseLegacyMode: false,
  mailServiceType: MailServiceType.IMAPSMTP
}

const hostName = helpers.regex('hostName', /^(([a-zA-Z_\-0-9]+)(\.([a-zA-Z_\-0-9]+))?)*$/)

// Helpers to restrict validators to the mail service type they relate to
const validateNotExchange = (value, vm) => vm.mailServiceType !== MailServiceType.EXCHANGE
const validateNotImap = (value, vm) => vm.mailServiceType !== MailServiceType.IMAPSMTP

export default {
  name: 'mail-service',
  props: ['serviceId'],
  components: { MainSection, PasswordInput },

  data() {
    return {
      service: {
        serviceType: 'MAIL',
        serviceConfiguration: _.clone(blankServiceConfiguration)
      },
      serviceDiff: { serviceConfiguration: {} },
      isOutdated: false,
      mailServiceTypes: MailServiceType
    }
  },

  validations: {
    service: {
      name: { required },
      key: { required },
      serviceConfiguration: {
        imapHost: { valid: or(validateNotImap, and(required, hostName)) },
        imapPort: { valid: or(validateNotImap, and(required, numeric, maxValue(65536))) },
        imapUsername: { valid: or(validateNotImap, required) },
        smtpHost: { valid: or(validateNotImap, and(required, hostName)) },
        smtpPort: { valid: or(validateNotImap, and(required, numeric, maxValue(65536))) },
        exchangeUser: { valid: or(validateNotExchange, required) }
      }
    }
  },

  computed: {
    allFlows() {
      return this.$store.state.flow.items
    },
    mode() {
      return this.service && this.service.id ? 'edit' : 'create'
    },

    usedIn() {
      return _.map(
        _.filter(
          this.allFlows,
          f =>
            f.typeConfig &&
            f.typeConfig.triggers &&
            f.typeConfig.triggers.services &&
            _.find(
              f.typeConfig.triggers.services,
              s =>
                s.serviceType === 'MAIL' &&
                this.service &&
                s.configuration.serviceId === this.service.id
            )
        ),
        f => {
          return {
            as: 'trigger',
            name: f.name,
            routeName: 'app-flow',
            routeParams: {
              appId: f.application,
              flowId: f.id,
              mode: 'settings'
            }
          }
        }
      )
    },
    isEditMode() {
      return this.mode === 'edit'
    },
    isImapMode() {
      return this.service.serviceConfiguration.mailServiceType === MailServiceType.IMAPSMTP
    },
    isExchangeMode() {
      return this.service.serviceConfiguration.mailServiceType === MailServiceType.EXCHANGE
    }
  },

  methods: {
    updateService() {
      if (!this.$v.$invalid) {
        if (!this.isOutdated) {
          return Service.updateService(this.serviceId, this.service)
            .then(res => {
              this.service.version = res.version
              Toast.success(this.$i18n.t('UPDATED', { thing: this.$i18n.tc('SERVICE', 1) }), {
                remove: 5000
              })
            })
            .catch(err => {
              if (err.code === ErrorCodes.ITEM_OUTDATED) {
                this.isOutdated = true
                Toast.error(
                  this.$i18n.t('ERR_OUTDATED_VERSION', {
                    action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('SERVICE', 1) }),
                    thing: this.$i18n.tc('SERVICE', 1)
                  })
                )
              } else {
                Toast.error(
                  this.$i18n.t('ERR', {
                    action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('SERVICE', 1) })
                  })
                )
                console.error(err)
              }
              throw err
            })
        } else {
          Toast.error(
            this.$i18n.t('ERR_OUTDATED_VERSION', {
              action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('USER', 1) }),
              thing: this.$i18n.tc('USER', 1)
            })
          )
          return Promise.reject('Outdated version')
        }
      } else {
        this.$v.$touch()
        Toast.warning(
          this.$i18n.t('WARN_REQUIRED', {
            action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('SERVICE', 1) }),
            thing: this.$i18n.t('ALL_REQUIRED_FIELDS')
          })
        )
        return Promise.reject('Required fields missing')
      }
    },

    createService() {
      if (!this.$v.$invalid) {
        return Service.createService(this.service)
          .then(res => {
            this.service.id = res.id
            this.service.version = res.version
          })
          .then(() => {
            Toast.success(this.$i18n.t('CREATED', { thing: this.$i18n.tc('SERVICE', 1) }), {
              remove: 5000
            })
            this.$router.push({
              name: 'service',
              params: { serviceId: this.service.id, serviceType: 'MAIL' }
            })
          })
          .catch(err => {
            Toast.error(
              this.$i18n.t('ERR', {
                action: this.$i18n.t('CREATE', { thing: this.$i18n.tc('SERVICE', 1) })
              }),
              { remove: 5000 }
            )
            console.error(err)
          })
      } else {
        this.$v.$touch()
        Toast.warning(
          this.$i18n.t('WARN_REQUIRED', {
            action: this.$i18n.t('CREATE', { thing: this.$i18n.tc('SERVICE', 1) }),
            thing: this.$i18n.t('ALL_REQUIRED_FIELDS')
          })
        )
        return Promise.reject('Required fields missing')
      }
    },

    deleteService() {
      Service.deleteService(this.service.id)
        .then(() => {
          Toast.success(this.$i18n.t('DELETED', { thing: this.$i18n.tc('SERVICE', 1) }), {
            remove: 5000
          })
          this.$router.push({
            name: 'services'
          })
        })
        .catch(err => {
          Toast.error(
            this.$i18n.t('ERR', {
              action: this.$i18n.t('DELETE', { thing: this.$i18n.tc('SERVICE', 1) })
            })
          )
          console.error(err)
        })
    },

    readService(id) {
      return Service.readService(id)
        .then(s => {
          this.service = _.merge(
            { serviceConfiguration: _.clone(blankServiceConfiguration) },
            s || {}
          )
          this.isOutdated = false
          Vue.nextTick(this.$v.$touch)
          return this.service
        })
        .catch(err => {
          Toast.error(
            this.$i18n.t('ERR', {
              action: this.$i18n.t('READ', { thing: this.$i18n.tc('SERVICE', 1) })
            })
          )
          console.error(err)
        })
    },

    editMode() {
      this.$v.$reset()
      this.readService(this.$route.params.serviceId)
    },

    createMode() {
      this.$v.$reset()
      this.service = {
        serviceType: 'MAIL',
        serviceConfiguration: _.clone(blankServiceConfiguration)
      }
    },
    onChangeChosen({ changedItem, fullDiff }) {
      this.service = changedItem
      this.serviceDiff = !_.isEmpty(fullDiff) ? fullDiff : { serviceConfiguration: {} }
    },
    onRevert(service) {
      this.service = service
      this.serviceDiff = { serviceConfiguration: {} }
    }
  },
  created() {
    if (this.$route.params.serviceId) {
      this.$v.$reset()
      this.readService(this.$route.params.serviceId).then(() => {})
    }
    Flow.readAllFlows()
  },

  watch: {
    'service.serviceConfiguration.mailServiceType': function(newServiceType) {
      this.service.serviceConfiguration.type = MailServiceTypeLabel[newServiceType]
    },
    '$route.params.serviceId': function(newServiceId) {
      if (newServiceId) {
        this.editMode()
      } else {
        this.createMode()
      }
    }
  }
}
</script>
